import { useRef, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { PROCESS, REVIEWS, FAQ, BUSINESS } from "@/data";
import { Reveal, Eyebrow } from "@/components/site/motion-lib";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

/* ---------------- Process timeline (SVG draw) ---------------- */
export function Process() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 70%", "end 60%"] });
  const pathLength = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section id="processo" ref={ref} data-testid="process-section" className="relative py-24 md:py-32 bg-[#1c1c1c] border-y border-[#F5F1EA]/10">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-2xl">
          <Reveal><Eyebrow>Come lavoriamo</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#F5F1EA] mt-5 text-balance">
              Dal primo squillo all'installazione
            </h2>
          </Reveal>
        </div>

        <div className="relative mt-16">
          {/* SVG connecting line */}
          <svg className="absolute left-0 top-10 h-14 w-full hidden lg:block pointer-events-none" preserveAspectRatio="none" viewBox="0 0 1000 4" aria-hidden="true">
            <line x1="0" y1="2" x2="1000" y2="2" stroke="rgba(245,241,234,0.1)" strokeWidth="2" />
            <motion.line x1="0" y1="2" x2="1000" y2="2" stroke="#C89B4A" strokeWidth="2" style={{ pathLength }} />
          </svg>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6 lg:pt-10">
            {PROCESS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.1}>
                <div className="relative">
                  <div className="flex items-center gap-4 lg:block">
                    <span className="relative z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#141414] border border-[#C89B4A]/50 font-display text-lg font-bold text-[#C89B4A]">
                      {p.n}
                    </span>
                    <h3 className="font-display text-xl font-bold text-[#F5F1EA] lg:mt-6">{p.title}</h3>
                  </div>
                  <p className="mt-7 text-[#F5F1EA]/60 leading-relaxed text-[15px]">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Reviews carousel ---------------- */
export function Reviews() {
  const [i, setI] = useState(0);
  const [dir, setDir] = useState(1);
  const n = REVIEWS.length;
  const go = (d) => { setDir(d); setI((prev) => (prev + d + n) % n); };
  const r = REVIEWS[i];

  return (
    <section id="recensioni" data-testid="reviews-section" className="relative py-24 md:py-32">
      <div className="mx-auto max-w-5xl px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div>
            <Reveal><Eyebrow>Recensioni</Eyebrow></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#F5F1EA] mt-5">
                Chi ci ha scelto
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="flex items-center gap-4">
              <span className="font-display text-6xl font-bold text-[#C89B4A]">4,7</span>
              <div>
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, k) => <Star key={k} size={16} className="fill-[#C89B4A] text-[#C89B4A]" />)}
                </div>
                <p className="text-sm text-[#F5F1EA]/55 mt-1">su 200+ recensioni Google</p>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="relative mt-12 metal-border rounded-2xl p-8 md:p-12 min-h-[280px] flex flex-col justify-between overflow-hidden">
          <Quote size={64} strokeWidth={1} className="absolute -top-2 right-6 text-[#C89B4A]/10" />
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div
              key={i}
              custom={dir}
              initial={{ opacity: 0, x: dir * 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: dir * -40 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              data-testid="review-slide"
            >
              <div className="flex gap-0.5 mb-5">
                {[...Array(r.stars)].map((_, k) => <Star key={k} size={18} className="fill-[#C89B4A] text-[#C89B4A]" />)}
              </div>
              <p className="font-display text-xl md:text-2xl text-[#F5F1EA] leading-relaxed">“{r.text}”</p>
              <p className="mt-6 text-sm font-semibold text-[#C89B4A]">{r.name}</p>
            </motion.div>
          </AnimatePresence>

          <div className="flex items-center justify-between mt-8">
            <div className="flex gap-2">
              {REVIEWS.map((_, k) => (
                <button
                  key={k}
                  aria-label={`Recensione ${k + 1}`}
                  onClick={() => { setDir(k > i ? 1 : -1); setI(k); }}
                  className={`h-1.5 rounded-full transition-all duration-300 ${k === i ? "w-8 bg-[#C89B4A]" : "w-2 bg-[#F5F1EA]/25"}`}
                />
              ))}
            </div>
            <div className="flex gap-2">
              <button data-testid="review-prev" onClick={() => go(-1)} aria-label="Precedente" className="grid h-11 w-11 place-items-center rounded-full border border-[#F5F1EA]/15 text-[#F5F1EA] hover:border-[#C89B4A] hover:text-[#C89B4A] transition-colors">
                <ChevronLeft size={20} strokeWidth={1.5} />
              </button>
              <button data-testid="review-next" onClick={() => go(1)} aria-label="Successiva" className="grid h-11 w-11 place-items-center rounded-full border border-[#F5F1EA]/15 text-[#F5F1EA] hover:border-[#C89B4A] hover:text-[#C89B4A] transition-colors">
                <ChevronRight size={20} strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Zone + map ---------------- */
export function Zone() {
  return (
    <section id="zona" data-testid="zone-section" className="relative py-24 md:py-32 bg-[#1c1c1c] border-y border-[#F5F1EA]/10">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-2xl">
          <Reveal><Eyebrow>Dove interveniamo</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#F5F1EA] mt-5 text-balance">
              Da Montespertoli in tutta la Toscana
            </h2>
          </Reveal>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
          <Reveal delay={0.1}>
            <div className="metal-border rounded-2xl overflow-hidden h-[300px] md:h-[440px]">
              <img
                src="https://customer-assets-rejwkqb3.emergentagent.net/job_fabbro-24h-premium/artifacts/wtwwjehj_IMG_9338.webp"
                alt="Ingresso della sede di Officine Galloni a Montespertoli"
                data-testid="zone-photo"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="metal-border rounded-2xl overflow-hidden h-[300px] md:h-[440px]">
              <iframe
                title="Mappa officina Officine Galloni"
                data-testid="map-embed"
                src={BUSINESS.maps}
                className="w-full h-full"
                style={{ border: 0, filter: "invert(0.9) hue-rotate(180deg) contrast(0.9)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------------- FAQ ---------------- */
export function Faq() {
  return (
    <section id="faq" data-testid="faq-section" className="relative py-24 md:py-32">
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <div className="text-center mb-14">
          <Reveal className="flex justify-center"><Eyebrow>Domande frequenti</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#F5F1EA] mt-5">
              Le risposte, senza giri di parole
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {FAQ.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-[#F5F1EA]/10">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-display text-lg md:text-xl font-semibold text-[#F5F1EA] hover:text-[#C89B4A] hover:no-underline py-6">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#F5F1EA]/65 leading-relaxed text-base pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
}
